import type { Connection, ConnectionId, ConnectionType, ObjectId } from '@/types';

/**
 * Pure graph algorithms over connections. Nothing here knows about rendering,
 * stores or React; the inputs are plain records and the outputs plain data.
 */

/** Relationship types that must not form cycles (a cannot depend on something that depends on a). */
export const ACYCLIC_TYPES: ReadonlySet<ConnectionType> = new Set(['depends-on', 'contains', 'blocks']);

/** Types drawn without a direction: the arrowhead would imply an order that does not exist. */
export const SYMMETRIC_TYPES: ReadonlySet<ConnectionType> = new Set(['related-to', 'linked-to']);

export type ConnectionRecord = Readonly<Record<ConnectionId, Connection>>;

export const isSymmetric = (type: ConnectionType): boolean => SYMMETRIC_TYPES.has(type);

/** Outgoing adjacency for the given types (all types when omitted). */
export const adjacency = (
  connections: ConnectionRecord,
  types?: ReadonlySet<ConnectionType>,
): ReadonlyMap<ObjectId, readonly ObjectId[]> => {
  const out = new Map<ObjectId, ObjectId[]>();
  for (const c of Object.values(connections)) {
    if (types && !types.has(c.type)) continue;
    out.set(c.sourceId, [...(out.get(c.sourceId) ?? []), c.targetId]);
  }
  return out;
};

/** Every object reachable from `start` by following outgoing edges of the given types. */
export const reachableFrom = (
  connections: ConnectionRecord,
  start: ObjectId,
  types?: ReadonlySet<ConnectionType>,
): ReadonlySet<ObjectId> => {
  const adj = adjacency(connections, types);
  const seen = new Set<ObjectId>();
  const stack = [start];
  while (stack.length) {
    const id = stack.pop();
    if (id === undefined) break;
    for (const next of adj.get(id) ?? []) {
      if (!seen.has(next)) {
        seen.add(next);
        stack.push(next);
      }
    }
  }
  return seen;
};

/**
 * True when adding `source → target` of `type` would close a cycle among acyclic
 * types. Symmetric and free-form types never cycle by definition.
 */
export const wouldCreateCycle = (
  connections: ConnectionRecord,
  source: ObjectId,
  target: ObjectId,
  type: ConnectionType,
  ignoreId?: ConnectionId,
): boolean => {
  if (!ACYCLIC_TYPES.has(type)) return false;
  if (source === target) return true;
  const considered: Record<ConnectionId, Connection> = {};
  for (const [id, c] of Object.entries(connections) as [ConnectionId, Connection][]) {
    if (id !== ignoreId && c.type === type) considered[id] = c;
  }
  return reachableFrom(considered, target, new Set([type])).has(source);
};

/** Every connection touching an object (either end). */
export const connectionsOf = (connections: ConnectionRecord, id: ObjectId): Connection[] =>
  Object.values(connections).filter((c) => c.sourceId === id || c.targetId === id);

/** Neighbouring objects regardless of direction. */
export const neighborsOf = (connections: ConnectionRecord, id: ObjectId): ObjectId[] => [
  ...new Set(connectionsOf(connections, id).map((c) => (c.sourceId === id ? c.targetId : c.sourceId))),
];

/** Existing connection between two objects in either direction, if any. */
export const findBetween = (connections: ConnectionRecord, a: ObjectId, b: ObjectId): Connection | undefined =>
  Object.values(connections).find(
    (c) => (c.sourceId === a && c.targetId === b) || (c.sourceId === b && c.targetId === a),
  );

/** Shortest undirected path between two objects (BFS), or null. */
export const findPath = (connections: ConnectionRecord, from: ObjectId, to: ObjectId): ObjectId[] | null => {
  if (from === to) return [from];
  const prev = new Map<ObjectId, ObjectId | null>([[from, null]]);
  const queue = [from];
  while (queue.length) {
    const id = queue.shift();
    if (id === undefined) break;
    for (const next of neighborsOf(connections, id)) {
      if (prev.has(next)) continue;
      prev.set(next, id);
      if (next === to) {
        const path: ObjectId[] = [];
        for (let cur: ObjectId | null = to; cur !== null; cur = prev.get(cur) ?? null) path.unshift(cur);
        return path;
      }
      queue.push(next);
    }
  }
  return null;
};

/** Cycles among a type's edges, each reported once as the ids involved; used by validation. */
export const findCycles = (connections: ConnectionRecord, type: ConnectionType): ObjectId[][] => {
  const adj = adjacency(connections, new Set([type]));
  const state = new Map<ObjectId, 'visiting' | 'done'>();
  const cycles: ObjectId[][] = [];
  const path: ObjectId[] = [];
  const visit = (id: ObjectId): void => {
    state.set(id, 'visiting');
    path.push(id);
    for (const next of adj.get(id) ?? []) {
      const s = state.get(next);
      if (s === 'visiting') cycles.push(path.slice(path.indexOf(next)));
      else if (s === undefined) visit(next);
    }
    path.pop();
    state.set(id, 'done');
  };
  for (const id of adj.keys()) if (!state.has(id)) visit(id);
  return cycles;
};
